/**
 * Frontend encryption utility
 * Uses AES-256-CBC symmetric encryption (via Web Crypto API)
 * 
 * ⚠️ SECURITY WARNING:
 * Client-side encryption does NOT provide true security.
 * The encryption key is available in the frontend code,
 * so determined users can still decrypt the data.
 * 
 * This implementation is for:
 * - Obfuscation of request/response data
 * - Deterring casual inspection via DevTools
 * - Practice/internal tools
 * 
 * NOT suitable for:
 * - Highly sensitive data
 * - Financial transactions
 * - Personal identifiable information (PII)
 */

const ALGORITHM = 'AES-CBC';
const KEY_LENGTH = 256;
const IV_LENGTH = 16;

/**
 * Efficiently convert ArrayBuffer to base64 without stack overflow
 * Uses chunking to handle large buffers
 */
function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000; // 32KB chunks
    let binary = '';
    
    for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
        // Convert chunk to binary string using apply to avoid stack overflow
        binary += String.fromCharCode.apply(null, Array.from(chunk));
    }
    
    return btoa(binary);
}

/**
 * Get encryption key from environment
 * Falls back to default for development (NOT for production)
 * 
 * Key derivation matches backend: SHA-256 hash of the key string
 */
async function getEncryptionKey() {
    // Ensure Web Crypto API is available
    // eslint-disable-next-line no-undef
    const cryptoObj = (typeof window !== 'undefined' && window.crypto) || (typeof globalThis !== 'undefined' && globalThis.crypto);
    const subtle = cryptoObj && cryptoObj.subtle;
    
    if (!cryptoObj || !subtle) {
        const errorMsg = 'Web Crypto API is not available. This feature requires HTTPS or a secure context (localhost is allowed).';
        console.error(errorMsg, { 
            hasWindow: typeof window !== 'undefined',
            hasCrypto: !!cryptoObj,
            hasSubtle: !!subtle,
            location: typeof window !== 'undefined' ? window.location.href : 'N/A'
        });
        throw new Error(errorMsg);
    }
    
    const keyString = process.env.REACT_APP_ENCRYPTION_KEY || 'default-dev-key-change-in-production';
    
    // Hash the key string with SHA-256 to match backend key derivation
    const encoder = new TextEncoder();
    const keyData = encoder.encode(keyString);
    const hashBuffer = await subtle.digest('SHA-256', keyData);
    
    // Import the hashed key as a raw key for AES-CBC
    const key = await subtle.importKey(
        'raw',
        hashBuffer,
        { name: ALGORITHM },
        false,
        ['encrypt', 'decrypt']
    );

    return key;
}

/**
 * Encrypt data
 * @param {string} text - Plain text to encrypt
 * @returns {Promise<string>} - Encrypted string (base64 encoded with IV)
 * Format: "IV:encrypted" (matches backend format)
 */
async function encrypt(text) {
    try {
        // Ensure Web Crypto API is available
        // eslint-disable-next-line no-undef
        const cryptoObj = (typeof window !== 'undefined' && window.crypto) || (typeof globalThis !== 'undefined' && globalThis.crypto);
        const subtle = cryptoObj && cryptoObj.subtle;
        
        if (!cryptoObj || !subtle) {
            throw new Error('Web Crypto API is not available. This feature requires HTTPS or a secure context (localhost is allowed).');
        }
        
        const key = await getEncryptionKey();
        const encoder = new TextEncoder();
        const data = encoder.encode(text);

        // Generate random IV
        const iv = cryptoObj.getRandomValues(new Uint8Array(IV_LENGTH));

        // Encrypt
        const encrypted = await subtle.encrypt(
            { name: ALGORITHM, iv: iv },
            key,
            data
        );

        // Convert IV and encrypted data to base64 separately
        // Format: "IV:encrypted" (matches backend format)
        // Use efficient conversion to avoid stack overflow with large arrays
        const ivBase64 = arrayBufferToBase64(iv.buffer || iv);
        
        // Convert encrypted data to base64
        const encryptedBase64 = arrayBufferToBase64(encrypted);

        return ivBase64 + ':' + encryptedBase64;
    } catch (error) {
        console.error('Encryption error:', error);
        throw new Error('Failed to encrypt data');
    }
}

/**
 * Decrypt data
 * @param {string} encryptedText - Encrypted string (base64 encoded with IV)
 * Format: "IV:encrypted" (matches backend format)
 * @returns {Promise<string>} - Decrypted plain text
 */
async function decrypt(encryptedText) {
    try {
        // Ensure Web Crypto API is available
        // eslint-disable-next-line no-undef
        const cryptoObj = (typeof window !== 'undefined' && window.crypto) || (typeof globalThis !== 'undefined' && globalThis.crypto);
        const subtle = cryptoObj && cryptoObj.subtle;
        
        if (!cryptoObj || !subtle) {
            throw new Error('Web Crypto API is not available. This feature requires HTTPS or a secure context (localhost is allowed).');
        }
        
        const key = await getEncryptionKey();
        const decoder = new TextDecoder();

        // Backend format: "IV:encrypted" (colon-separated)
        const parts = encryptedText.split(':');
        
        if (parts.length !== 2) {
            throw new Error('Invalid encrypted data format: expected "IV:encrypted"');
        }

        // Decode IV and encrypted data from base64
        const ivBase64 = parts[0].trim();
        const encryptedBase64 = parts[1].trim();

        // Validate base64 strings before decoding
        if (!ivBase64 || !encryptedBase64) {
            throw new Error('Empty IV or encrypted data');
        }

        // Validate base64 format (basic check)
        const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
        if (!base64Regex.test(ivBase64) || !base64Regex.test(encryptedBase64)) {
            throw new Error('Invalid base64 format in encrypted data');
        }

        let iv, encrypted;
        try {
            // Decode IV (small, can use fromCharCode)
            const ivString = atob(ivBase64);
            iv = new Uint8Array(ivString.length);
            for (let i = 0; i < ivString.length; i++) {
                iv[i] = ivString.charCodeAt(i);
            }
            
            // Decode encrypted data (may be large, use loop instead of fromCharCode)
            const encryptedString = atob(encryptedBase64);
            encrypted = new Uint8Array(encryptedString.length);
            for (let i = 0; i < encryptedString.length; i++) {
                encrypted[i] = encryptedString.charCodeAt(i);
            }
        } catch (e) {
            throw new Error('Failed to decode base64: ' + e.message);
        }

        // Validate IV length
        if (iv.length !== IV_LENGTH) {
            throw new Error(`Invalid IV length: expected ${IV_LENGTH}, got ${iv.length}`);
        }

        // Decrypt
        const decrypted = await subtle.decrypt(
            { name: ALGORITHM, iv: iv },
            key,
            encrypted
        );

        return decoder.decode(decrypted);
    } catch (error) {
        console.error('Decryption error:', error);
        throw new Error('Failed to decrypt data: ' + error.message);
    }
}

/**
 * Encrypt JSON object
 * @param {object} data - Object to encrypt
 * @returns {Promise<string>} - Encrypted string
 */
async function encryptJSON(data) {
    const jsonString = JSON.stringify(data);
    return await encrypt(jsonString);
}

/**
 * Decrypt and parse JSON
 * @param {string} encryptedData - Encrypted string
 * @returns {Promise<object>} - Decrypted object
 */
async function decryptJSON(encryptedData) {
    const decrypted = await decrypt(encryptedData);
    return JSON.parse(decrypted);
}

export {
    encrypt,
    decrypt,
    encryptJSON,
    decryptJSON
};

